import React, { useState } from "react";
import { Pencil, Check, Eye, EyeOff } from "lucide-react";
import { staffAPI } from "../../services/AdminPortal/staffAPI";
import { toast } from "react-hot-toast";

const EditStaffModal = ({ isOpen, onClose, staffData, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: staffData?.name || "",
    role: staffData?.role || "",
    email: staffData?.email || "",
    phone: staffData?.phone || "",
    dob: staffData?.dob || "",
    createdAt: staffData?.createdAt || "",
    location: staffData?.location || "",
    profilePicture: staffData?.profilePicture || "", // Base64 or URL
    password: "", // Added password
    status: staffData?.status || "active",
    targetLoans: staffData?.targetLoans || 0,
  });

  const [showPassword, setShowPassword] = useState(false); // toggle password visibility
  const [errors, setErrors] = useState({});

  const validateDateFormat = (dateString) => {
    if (!dateString) return "";
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) return "Date must be in YYYY-MM-DD format";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Please enter a valid date";
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));

    if (name === "dob") {
      const error = validateDateFormat(value);
      if (error) setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({ ...prev, profilePicture: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Password validation (optional)
    if (formData.password && formData.password.length < 6) {
      setErrors({ password: "Password must be at least 6 characters" });
      return;
    }

    try {
      const updates = { ...formData };
      if (!updates.password) delete updates.password; // Do not send empty password
      await staffAPI.updateStaff(staffData.staffId, updates);
      onUpdate(updates);
      toast.success("Staff Profile updated successfully!");

      onClose();
    } catch (error) {
      toast.error(error.message);
      console.error("Error updating staff details:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-[#131313] text-white p-6 rounded-lg w-full max-w-2xl overflow-y-auto max-h-[90vh]">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Pencil size={18} className="text-yellow-400" />
          Edit Profile [{staffData?.name || "Staff Name"}]
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Top Row: Profile Pic + Name + Role */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="relative">
              <img
                src={formData.profilePicture}
                alt="Profile"
                className="w-20 h-20 rounded-full object-cover"
              />
              <label className="absolute bottom-0 right-0 bg-yellow-400 text-black p-1 rounded-full border-2 border-[#1e1e1e] cursor-pointer">
                <Pencil size={14} />
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </label>
            </div>
            <div className="flex-1">
              <label className="block text-sm mb-1">Staff Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full bg-[#131313] border border-[#333] rounded p-2"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <div className="flex-1">
                <label className="block text-sm mb-1">Role</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full bg-[#131313] border border-[#333] rounded p-2"
                >
                  <option value="admin">Admin</option>
                  <option value="loanOfficer">Loan Officer</option>
                  <option value="subAdmin">Sub Admin</option>
                  <option value="hr">HR</option>
                  <option value="credit-manager">Credit Manager</option>
                  <option value="marketing">Marketing</option>
                  <option value="sales">Sales</option>
                  <option value="strategy">Strategy</option>
                  <option value="technical">Technical</option>
                  <option value="research">Research</option>
                </select>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="flex-1">
              <label className="block text-sm mb-1">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full bg-[#131313] border border-[#333] rounded p-2"
              >
                <option value="active">active</option>
                <option value="inactive">inactive</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm mb-1">Change Target Loans</label>
              <input
                type="tel"
                name="targetLoans"
                value={formData.targetLoans}
                onChange={handleChange}
                className="w-full bg-[#131313] border border-[#333] rounded p-2"
              />
            </div>
          </div>

          {/* Email + Phone */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm mb-1">Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full bg-[#131313] border border-[#333] rounded p-2"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm mb-1">Contact Number</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full bg-[#131313] border border-[#333] rounded p-2"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            {/* Password with toggle */}
            <div className="flex-1 relative">
              <label className="block text-sm mb-1">Change Password</label>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter new password"
                className={`w-full bg-[#131313] border rounded p-2 ${
                  errors.password ? "border-red-500" : "border-[#333]"
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-2 top-[38px] text-gray-400"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">{errors.password}</p>
              )}
            </div>
            {/* Location */}
            <div className="flex-1">
              <label className="block text-sm mb-1">Location</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className="w-full bg-[#131313] border border-[#333] rounded p-2"
              />
            </div>
          </div>

          {/* Date Fields */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm mb-1">Date of Joining</label>
              <input
                type="date"
                name="createdAt"
                value={formData.createdAt}
                onChange={handleChange}
                className="w-full bg-[#131313] border border-[#333] rounded p-2"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm mb-1">Date of Birth</label>
              <input
                type="date"
                name="dob"
                value={formData.dob}
                onChange={handleChange}
                className={`w-full bg-[#131313] border rounded p-2 ${
                  errors.dob ? "border-red-500" : "border-[#333]"
                }`}
              />
              {errors.dob && (
                <p className="text-red-500 text-xs mt-1">{errors.dob}</p>
              )}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-between mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-white text-white rounded hover:bg-red-500 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#FFCA20] text-black rounded flex items-center gap-2 hover:bg-yellow-300 transition"
            >
              Save Changes <Check size={16} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditStaffModal;
